import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeacherCourses } from "../../../redux/Actions/adminDeveloperTeacherCoursesAction";
import AdminNavbar from "./AdminNavbar";
import api from "lib/api/api";

const TeacherCourseManager = () => {
  const dispatch = useDispatch();
  const { loading, data, error } = useSelector((s) => s.teacherCourses);

  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [courseCapacities, setCourseCapacities] = useState([]);
  const [courseCapacityDrafts, setCourseCapacityDrafts] = useState({});
  const [teacherCapacityDrafts, setTeacherCapacityDrafts] = useState({});
  const [enrollmentCountDrafts, setEnrollmentCountDrafts] = useState({});

  // modal state for errors
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  useEffect(() => {
    dispatch(fetchTeacherCourses());
    api.get("/admin-developer/course-capacities").then(({ data: rows }) => {
      const capacities = rows || [];
      setCourseCapacities(capacities);
      setCourseCapacityDrafts(Object.fromEntries(capacities.map((row) => [row.courseId, row.courseMaxStudents || 0])));
    }).catch((err) => {
      setErrorModalMessage(err.response?.data?.message || "Failed to load course capacities");
      setErrorModalOpen(true);
    });
  }, [dispatch]);

  useEffect(() => {
    setTeacherCapacityDrafts(Object.fromEntries((data || []).map((row) => [row.id, row.teacherCourseMaxStudents || 0])));
    setEnrollmentCountDrafts(Object.fromEntries((data || []).map((row) => [row.id, row.enrolledCount || 0])));
  }, [data]);

  const saveCourseCapacity = async (courseId) => {
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/course/${courseId}/capacity`, { maxStudents: Number(courseCapacityDrafts[courseId] || 0) });
      setSuccessMessage("Default course capacity updated.");
      const { data: rows } = await api.get("/admin-developer/course-capacities");
      setCourseCapacities(rows || []);
      dispatch(fetchTeacherCourses());
    } catch (err) {
      setErrorModalMessage(err.response?.data?.message || "Failed to update course capacity");
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const saveTeacherCourseCapacity = async (teacherCourseId) => {
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/teacher-course/${teacherCourseId}/capacity`, { maxStudents: Number(teacherCapacityDrafts[teacherCourseId] || 0) });
      setSuccessMessage("Teacher-course capacity updated.");
      dispatch(fetchTeacherCourses());
    } catch (err) {
      setErrorModalMessage(err.response?.data?.message || "Failed to update teacher-course capacity");
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const saveEnrollmentCount = async (teacherCourseId) => {
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/teacher-course/${teacherCourseId}/enrolled-count`, { enrolledStudents: Number(enrollmentCountDrafts[teacherCourseId] || 0) });
      setSuccessMessage("Enrolled-student count updated.");
      dispatch(fetchTeacherCourses());
    } catch (err) {
      setErrorModalMessage(err.response?.data?.message || "Failed to update enrolled-student count");
      setErrorModalOpen(true);
    } finally { setIsLoadingAction(false); }
  };

  const retrieveActualEnrollmentCount = async (teacherCourseId) => {
    try {
      setIsLoadingAction(true);
      const response = await api.post(`/admin-developer/teacher-course/${teacherCourseId}/enrolled-count/retrieve-actual`);
      setEnrollmentCountDrafts((current) => ({ ...current, [teacherCourseId]: response.data?.enrolledCount || 0 }));
      setSuccessMessage("Actual count retrieved from active enrollments.");
      dispatch(fetchTeacherCourses());
    } catch (err) {
      setErrorModalMessage(err.response?.data?.message || "Failed to retrieve the actual enrollment count");
      setErrorModalOpen(true);
    } finally { setIsLoadingAction(false); }
  };

const handleArchive = async (id) => {
  try {
    setIsLoadingAction(true);
    await api.put(`/admin-developer/teacher-course/${id}/archive`);
    setSuccessMessage("Teacher course archived successfully!");
    dispatch(fetchTeacherCourses());
  } catch (err) {
    let backendMsg =
      err.response?.data?.message || err.message || "Error archiving teacher course";

    if (typeof backendMsg !== "string") {
      backendMsg = JSON.stringify(backendMsg);
    }

    setErrorModalMessage(backendMsg);
    setErrorModalOpen(true);
  } finally {
    setIsLoadingAction(false);
  }
};

const handleUnarchive = async (id) => {
  try {
    setIsLoadingAction(true);
    await api.put(`/admin-developer/teacher-course/${id}/unarchive`);
    setSuccessMessage("Teacher course unarchived successfully!");
    dispatch(fetchTeacherCourses());
  } catch (err) {
    let backendMsg =
      err.response?.data?.message || err.message || "Error unarchiving teacher course";

    if (typeof backendMsg !== "string") {
      backendMsg = JSON.stringify(backendMsg);
    }

    setErrorModalMessage(backendMsg);
    setErrorModalOpen(true);
  } finally {
    setIsLoadingAction(false);
  }
};

const handleDelete = async (id) => {
  try {
    setIsLoadingAction(true);
    const res = await api.delete(`/admin-developer/teacher-course/${id}`);
    let msg = res.data?.message || "Teacher course deleted successfully!";

    if (typeof msg !== "string") {
      msg = JSON.stringify(msg);
    }

    setSuccessMessage(msg);
    dispatch(fetchTeacherCourses());
  } catch (err) {
    let backendMsg =
      err.response?.data?.message || err.message || "Error deleting teacher course";

    if (typeof backendMsg !== "string") {
      backendMsg = JSON.stringify(backendMsg);
    }

    setErrorModalMessage(backendMsg);
    setErrorModalOpen(true);
  } finally {
    setIsLoadingAction(false);
  }
};

const handleBulkArchive = async () => {
  if (selectedIds.length === 0) return;
  try {
    setIsLoadingAction(true);
    await api.put(`/admin-developer/teacher-course/archive-bulk`, selectedIds);
    setSuccessMessage(`${selectedIds.length} courses archived successfully!`);
    setSelectedIds([]);
    dispatch(fetchTeacherCourses());
  } catch (err) {
    let backendMsg = err.response?.data?.message || err.message || "Error bulk archiving";
    setErrorModalMessage(typeof backendMsg === "string" ? backendMsg : JSON.stringify(backendMsg));
    setErrorModalOpen(true);
  } finally {
    setIsLoadingAction(false);
  }
};

const handleBulkUnarchive = async () => {
  if (selectedIds.length === 0) return;
  try {
    setIsLoadingAction(true);
    await api.put(`/admin-developer/teacher-course/unarchive-bulk`, selectedIds);
    setSuccessMessage(`${selectedIds.length} courses unarchived successfully!`);
    setSelectedIds([]);
    dispatch(fetchTeacherCourses());
  } catch (err) {
    let backendMsg = err.response?.data?.message || err.message || "Error bulk unarchiving";
    setErrorModalMessage(typeof backendMsg === "string" ? backendMsg : JSON.stringify(backendMsg));
    setErrorModalOpen(true);
  } finally {
    setIsLoadingAction(false);
  }
};

  // Auto-hide success messages after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage]);

  const filteredData = data.filter((tc) =>
    tc.courseTitle?.toLowerCase().includes(filterText.toLowerCase())
  );

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(filteredData.map((tc) => tc.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (event, id) => {
    if (event.target.checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  return (
    <div>
      <AdminNavbar />
      <Container>
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Teacher Courses
          </Typography>

          {/* Filter input */}
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <TextField
              label="Filter by Course Title"
              variant="outlined"
              size="small"
              sx={{ width: '300px' }}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            
            {selectedIds.length > 0 && (
              <Box>
                <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                  {selectedIds.length} selected
                </Typography>
                <Button 
                  variant="contained" 
                  color="warning" 
                  onClick={handleBulkArchive}
                  disabled={isLoadingAction}
                  sx={{ mr: 1 }}
                >
                  Archive Selected
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handleBulkUnarchive}
                  disabled={isLoadingAction}
                >
                  Unarchive Selected
                </Button>
              </Box>
            )}
          </Box>

          {loading && <Typography>Loading teacher courses...</Typography>}
          {error && <Typography color="error">{error}</Typography>}

          {isLoadingAction && <CircularProgress />}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          <Paper sx={{ p: 2, my: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Default capacity by course</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Teacher-course capacity 0 inherits this value. A bookable course must have an effective capacity.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Course</TableCell><TableCell>Default maximum students</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
                <TableBody>
                  {courseCapacities.map((course) => (
                    <TableRow key={course.courseId}>
                      <TableCell>{course.courseTitle}</TableCell>
                      <TableCell>
                        <TextField type="number" size="small" inputProps={{ min: 0 }} value={courseCapacityDrafts[course.courseId] ?? 0} onChange={(e) => setCourseCapacityDrafts((prev) => ({ ...prev, [course.courseId]: e.target.value }))} helperText="0 = not configured" />
                      </TableCell>
                      <TableCell><Button variant="contained" onClick={() => saveCourseCapacity(course.courseId)} disabled={isLoadingAction}>Save</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {filteredData && filteredData.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length}
                        checked={filteredData.length > 0 && selectedIds.length === filteredData.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Teacher Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Course Title</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>End Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Enrolled</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Course Default</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Teacher Override</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Effective Maximum</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Active</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((tc) => {
                    const isSelected = selectedIds.includes(tc.id);
                    return (
                    <TableRow key={tc.id} selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleSelectOne(e, tc.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {tc.teacherFirstName} {tc.teacherLastName}
                      </TableCell>
                      <TableCell>{tc.courseTitle}</TableCell>
                      <TableCell>
                        {new Date(tc.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(tc.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{tc.statusName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 210 }}>
                          <TextField type="number" size="small" inputProps={{ min: 0 }} value={enrollmentCountDrafts[tc.id] ?? 0} onChange={(e) => setEnrollmentCountDrafts((current) => ({ ...current, [tc.id]: e.target.value }))} />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => saveEnrollmentCount(tc.id)} disabled={isLoadingAction}>Save</Button>
                            <Button size="small" variant="text" onClick={() => retrieveActualEnrollmentCount(tc.id)} disabled={isLoadingAction}>Retrieve actual</Button>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{tc.courseMaxStudents || "Not configured"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1, minWidth: 190 }}>
                          <TextField type="number" size="small" inputProps={{ min: 0 }} value={teacherCapacityDrafts[tc.id] ?? 0} onChange={(e) => setTeacherCapacityDrafts((prev) => ({ ...prev, [tc.id]: e.target.value }))} helperText="0 = use default" />
                          <Button size="small" variant="outlined" onClick={() => saveTeacherCourseCapacity(tc.id)} disabled={isLoadingAction}>Save</Button>
                        </Box>
                      </TableCell>
                      <TableCell>{tc.effectiveMaxStudents || "Not configured"} <Typography component="span" variant="caption" color="text.secondary">({tc.capacitySource === "TEACHER_COURSE" ? "override" : "default"})</Typography></TableCell>
                      <TableCell>{tc.active ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {tc.active ? (
                          <Button
                            variant="contained"
                            color="warning"
                            onClick={() => handleArchive(tc.id)}
                            disabled={isLoadingAction}
                            sx={{ mr: 1 }}
                          >
                            Archive
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleUnarchive(tc.id)}
                            disabled={isLoadingAction}
                            sx={{ mr: 1 }}
                          >
                            Unarchive
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleDelete(tc.id)}
                          disabled={isLoadingAction}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            !loading && <Typography>No teacher courses available</Typography>
          )}
        </Box>
      </Container>

      {/* Error Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title">Action Failed</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorModalMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeacherCourseManager;
